import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import Button from './Button';
import Input from './Input';
import { DECISION_FRAMEWORK_TYPES } from '../../../../users/helpers/constants';

/*
 * Survey Completion Test
 * ----------------------
 * Programmatically replicates the web survey-taking flow
 * (/users/campaign/:surveyId/user/:userId/?test=true) and completes a survey
 * with random valid answers, N times. For each iteration it shows the status.
 *
 * The web survey GraphQL operations live on the USERS endpoint
 * (`${appUrl}/graphql/users`), which is different from the clients endpoint this
 * tool normally uses. These mutations are public (no auth header needed), exactly
 * as they are when an anonymous user takes a survey via a public link.
 *
 * The operations below mirror src/users/graphql/Campaigns.js. The per-question
 * answer generation mirrors the logic in
 * src/users/campaigns/components/Campaigns/CampaignQuestions/CampaignQuestionsPage.js.
 */

// Maps a survey's display language to the WebSurveyUserDataInput language code.
const LANGUAGE_MAP = {
  English: 'EN',
  Dutch: 'NL',
  French: 'FR',
  German: 'DE'
};

// Resolves the backend base URL the same way src/clients/graphqlClient.js does.
const getAppUrl = () => {
  const backend = localStorage.getItem('backend');
  if (backend === 'local') return 'http://localhost:8080';
  if (backend === 'test') return 'https://test.buffl.be';
  if (backend === 'prod') return 'https://buffl-prod-backend.azurewebsites.net';
  if (
    process.env.NODE_ENV === 'development' ||
    process.env.NODE_ENV === 'test'
  ) {
    return 'http://localhost:8080';
  }
  if (process.env.REACT_APP_ENV === 'qa') return 'https://test.buffl.be';
  return 'https://buffl-prod-backend.azurewebsites.net';
};

const gqlUsers = async (query, variables) => {
  const response = await fetch(`${getAppUrl()}/graphql/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables })
  });
  const json = await response.json();
  if (json.errors && json.errors.length) {
    throw new Error(json.errors.map(e => e.message).join('; '));
  }
  if (!json.data) {
    throw new Error('No data returned from the server.');
  }
  return json.data;
};

// Fields of a block (question) we need in order to generate a random answer.
const BLOCK_FIELDS = `
  id
  type
  random
  allowOther
  allowNoneOfTheAbove
  selectAtLeast
  selectAtMost
  answerMinCharacters
  answerMaxCharacters
  minValue
  maxValue
  forceNumberEnter
  answers {
    id
    decisionFrameworkType
    lockedPosition
  }
  matrix {
    decisionFrameworkType
    xQuestionId
    yQuestionId
    customScale {
      xScaleLabels
      yScaleLabels
    }
  }
`;

// Shared response shape returned by every submit mutation.
const RESULT_FIELDS = `
  nextBlock {
    ${BLOCK_FIELDS}
  }
  failure
  errors {
    field
    message
  }
  percentageCompleted
  result {
    id
  }
`;

const GET_CAMPAIGN = `
  query campaign($id: String!, $referrer: String!) {
    campaign(input: { id: $id, referrer: $referrer }) {
      id
      name
      language
      askForSocioDemoWebSurvey
    }
  }
`;

const START_WEB_SURVEY = `
  mutation StartWebSurvey(
    $survey: String!
    $referrer: String
    $userData: WebSurveyUserDataInput!
    $testing: Boolean
  ) {
    startWebSurvey(
      input: {
        survey: $survey
        referrer: $referrer
        userData: $userData
        testing: $testing
      }
    ) {
      nextBlock {
        ${BLOCK_FIELDS}
      }
      failure
      errors {
        field
        message
      }
      percentageCompleted
      result {
        id
      }
    }
  }
`;

// One submit mutation per web block type, matching src/users/graphql/Campaigns.js.
const SUBMIT_MESSAGE = `
  mutation submitMessageBlock($id: String!, $startedAt: DateTime!, $finishedAt: DateTime!, $result: String, $testing: Boolean) {
    submitMessageBlock(input: { id: $id, startedAt: $startedAt, result: $result, testing: $testing, finishedAt: $finishedAt }) {
      ${RESULT_FIELDS}
    }
  }
`;
const SUBMIT_VIDEO = `
  mutation SubmitVideoBlock($id: String!, $referrer: String, $startedAt: DateTime!, $finishedAt: DateTime!, $result: String, $testing: Boolean) {
    submitVideoBlock(input: { id: $id, referrer: $referrer, startedAt: $startedAt, result: $result, testing: $testing, finishedAt: $finishedAt }) {
      ${RESULT_FIELDS}
    }
  }
`;
const SUBMIT_IFRAME = `
  mutation SubmitIFrameBlock($id: String!, $startedAt: DateTime!, $finishedAt: DateTime!, $result: String, $referrer: String, $testing: Boolean) {
    submitIFrameBlock(input: { id: $id, startedAt: $startedAt, finishedAt: $finishedAt, referrer: $referrer, result: $result, testing: $testing }) {
      ${RESULT_FIELDS}
    }
  }
`;
const SUBMIT_OPEN_QUESTION = `
  mutation SubmitOpenQuestionBlock($id: String!, $referrer: String, $startedAt: DateTime!, $finishedAt: DateTime!, $result: String, $testing: Boolean, $answerString: String!) {
    submitOpenQuestionBlock(input: { id: $id, referrer: $referrer, startedAt: $startedAt, result: $result, testing: $testing, finishedAt: $finishedAt, answerString: $answerString }) {
      ${RESULT_FIELDS}
    }
  }
`;
const SUBMIT_MC_QUESTION = `
  mutation SubmitMultipleChoiceQuestion($id: String!, $answers: [Int!]!, $otherAnswer: String, $startedAt: DateTime!, $finishedAt: DateTime!, $result: String, $referrer: String, $testing: Boolean) {
    submitMultipleChoiceQuestion(input: { id: $id, answers: $answers, otherAnswer: $otherAnswer, referrer: $referrer, startedAt: $startedAt, result: $result, testing: $testing, finishedAt: $finishedAt }) {
      ${RESULT_FIELDS}
    }
  }
`;
const SUBMIT_SLIDER = `
  mutation SubmitSliderBlock($id: String!, $referrer: String, $answer: Int, $startedAt: DateTime!, $finishedAt: DateTime!, $result: String, $testing: Boolean) {
    submitSliderBlock(input: { id: $id, answer: $answer, referrer: $referrer, startedAt: $startedAt, result: $result, testing: $testing, finishedAt: $finishedAt }) {
      ${RESULT_FIELDS}
    }
  }
`;
const SUBMIT_MATRIX = `
  mutation submitMatrixBlock($id: String!, $result: String, $matrixAnswers: [MatrixAnswerInput!]!, $startedAt: DateTime!, $finishedAt: DateTime!, $testing: Boolean) {
    submitMatrixBlock(input: { id: $id, result: $result, matrixAnswers: $matrixAnswers, startedAt: $startedAt, finishedAt: $finishedAt, testing: $testing }) {
      ${RESULT_FIELDS}
    }
  }
`;

const randomInt = (min, max) => {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// Returns [0, 1, ... n-1] in random order.
const shuffledRange = n => {
  const arr = Array.from({ length: n }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const LOREM =
  'lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua';

const randomText = (target, min, max) => {
  let text = '';
  while (text.length < target) {
    text += (text ? ' ' : '') + LOREM;
  }
  text = text.slice(0, target).trim();
  if (min && text.length < min) text = text.padEnd(min, 'a');
  if (max && max > 0 && text.length > max) text = text.slice(0, max);
  return text || 'test';
};

const randomDigits = n => {
  const length = Math.max(1, n);
  let digits = String(randomInt(1, 9));
  while (digits.length < length) digits += String(randomInt(0, 9));
  return digits;
};

// Generates the WebSurveyUserDataInput. We always send gender/age/city so the
// run also passes surveys that ask for socio-demographics.
const buildUserData = language => ({
  language,
  gender: ['M', 'F', 'O'][randomInt(0, 2)],
  age: randomInt(18, 65),
  city: '1000'
});

// mc_question: answers is an array of indices into block.answers. The optional
// "Other" / "None of the above" options live at the end of block.answers, so we
// pick from the first `realCount` real answers and never need an other-answer.
const randomMultipleChoice = block => {
  const all = block.answers || [];
  const total = all.length;
  const realCount =
    total - (block.allowOther ? 1 : 0) - (block.allowNoneOfTheAbove ? 1 : 0);

  if (realCount <= 0) {
    if (block.allowNoneOfTheAbove && total > 0) {
      return { answers: [total - 1], otherAnswer: null };
    }
    if (block.allowOther && total > 0) {
      return { answers: [total - 1], otherAnswer: 'Test' };
    }
    return { answers: total > 0 ? [0] : [], otherAnswer: null };
  }

  let maxSelect =
    block.selectAtMost && block.selectAtMost > 0 ? block.selectAtMost : 1;
  let minSelect =
    block.selectAtLeast && block.selectAtLeast > 0 ? block.selectAtLeast : 1;
  maxSelect = Math.min(maxSelect, realCount);
  if (minSelect > maxSelect) minSelect = maxSelect;
  if (minSelect < 1) minSelect = 1;

  const count = randomInt(minSelect, maxSelect);
  const answers = shuffledRange(realCount).slice(0, count);
  return { answers, otherAnswer: null };
};

const randomOpenAnswer = block => {
  const min =
    block.answerMinCharacters && block.answerMinCharacters > 0
      ? block.answerMinCharacters
      : 0;
  const max =
    block.answerMaxCharacters && block.answerMaxCharacters > 0
      ? block.answerMaxCharacters
      : 0;

  let target;
  if (max > 0) {
    target = randomInt(Math.max(min, 1), Math.max(max, Math.max(min, 1)));
  } else {
    target = Math.max(min, randomInt(8, 20));
  }

  if (block.forceNumberEnter) return randomDigits(target);
  return randomText(target, min, max);
};

const randomSliderAnswer = block => {
  const min = Number.isInteger(block.minValue) ? block.minValue : 0;
  const max = Number.isInteger(block.maxValue) ? block.maxValue : 10;
  return randomInt(min, max);
};

// matrix: one entry per dimension (x, and y unless the framework is SINGLE),
// each with a random scale column index per row answer.
const randomMatrixAnswers = block => {
  const { matrix } = block;
  if (!matrix) return [];

  const frameworkType = matrix.decisionFrameworkType;
  const allRows = block.answers || [];
  const filteredRows = allRows.filter(
    a =>
      !frameworkType ||
      !a.decisionFrameworkType ||
      a.decisionFrameworkType === frameworkType
  );
  const rows = filteredRows.length ? filteredRows : allRows;

  const scaleLength = labels => (labels && labels.length ? labels.length : 4);
  const xColumns = scaleLength(
    matrix.customScale && matrix.customScale.xScaleLabels
  );
  const yColumns = scaleLength(
    matrix.customScale && matrix.customScale.yScaleLabels
  );

  const choicesFor = columns =>
    rows.map(answer => ({
      choiceId: answer.id,
      answers: [randomInt(0, Math.max(columns - 1, 0))]
    }));

  const matrixAnswers = [];
  if (matrix.xQuestionId) {
    matrixAnswers.push({
      questionId: matrix.xQuestionId,
      choices: choicesFor(xColumns)
    });
  }
  const isSingle = frameworkType === DECISION_FRAMEWORK_TYPES.SINGLE;
  if (matrix.yQuestionId && !isSingle) {
    matrixAnswers.push({
      questionId: matrix.yQuestionId,
      choices: choicesFor(yColumns)
    });
  }
  return matrixAnswers;
};

// Builds the submit mutation + variables for a given block with a random answer.
const buildSubmission = (block, resultId, userId) => {
  const now = new Date();
  const base = {
    id: block.id,
    result: resultId,
    startedAt: new Date(now.getTime() - 3000).toISOString(),
    finishedAt: now.toISOString(),
    testing: true
  };

  switch (block.type) {
    case 'message':
      return {
        query: SUBMIT_MESSAGE,
        responseKey: 'submitMessageBlock',
        variables: base
      };
    case 'video':
      return {
        query: SUBMIT_VIDEO,
        responseKey: 'submitVideoBlock',
        variables: { ...base, referrer: userId }
      };
    case 'iframe':
      return {
        query: SUBMIT_IFRAME,
        responseKey: 'submitIFrameBlock',
        variables: { ...base, referrer: userId }
      };
    case 'open_question':
      return {
        query: SUBMIT_OPEN_QUESTION,
        responseKey: 'submitOpenQuestionBlock',
        variables: {
          ...base,
          referrer: userId,
          answerString: randomOpenAnswer(block)
        }
      };
    case 'mc_question': {
      const { answers, otherAnswer } = randomMultipleChoice(block);
      return {
        query: SUBMIT_MC_QUESTION,
        responseKey: 'submitMultipleChoiceQuestion',
        variables: { ...base, referrer: userId, answers, otherAnswer }
      };
    }
    case 'slider':
      return {
        query: SUBMIT_SLIDER,
        responseKey: 'submitSliderBlock',
        variables: {
          ...base,
          referrer: userId,
          answer: randomSliderAnswer(block)
        }
      };
    case 'matrix':
      return {
        query: SUBMIT_MATRIX,
        responseKey: 'submitMatrixBlock',
        variables: { ...base, matrixAnswers: randomMatrixAnswers(block) }
      };
    default:
      // Unknown / non-web block type — submit it as a message to advance.
      return {
        query: SUBMIT_MESSAGE,
        responseKey: 'submitMessageBlock',
        variables: base
      };
  }
};

const ensureNoErrors = payload => {
  if (payload && payload.errors && payload.errors.length) {
    throw new Error(payload.errors.map(e => e.message).join('; '));
  }
};

// Runs a single full survey completion. onProgress is called after each answer.
const runIteration = async (surveyId, userId, language, onProgress) => {
  const startData = await gqlUsers(START_WEB_SURVEY, {
    survey: surveyId,
    referrer: userId,
    userData: buildUserData(language),
    testing: true
  });

  const start = startData.startWebSurvey;
  if (!start) throw new Error('startWebSurvey returned null.');
  ensureNoErrors(start);

  const resultId = start.result && start.result.id;
  if (!resultId) throw new Error('No result id returned from startWebSurvey.');

  let block = start.nextBlock;
  let { failure } = start;
  let percentage = start.percentageCompleted || 0;
  let answered = 0;

  if (failure)
    return { outcome: 'screened_out', answered, percentage, resultId };

  let guard = 0;
  while (block && block.id && !failure) {
    guard += 1;
    if (guard > 500) {
      throw new Error('Exceeded 500 questions — aborting (possible loop).');
    }

    const submission = buildSubmission(block, resultId, userId);
    // eslint-disable-next-line no-await-in-loop
    const data = await gqlUsers(submission.query, submission.variables);
    const payload = data[submission.responseKey];
    if (!payload) throw new Error(`${submission.responseKey} returned null.`);
    ensureNoErrors(payload);

    const { failure: nextFailure, percentageCompleted, nextBlock } = payload;
    answered += 1;
    failure = nextFailure;
    percentage = percentageCompleted != null ? percentageCompleted : percentage;
    block = nextBlock;

    if (onProgress) onProgress({ answered, percentage });
  }

  if (failure)
    return { outcome: 'screened_out', answered, percentage, resultId };
  return { outcome: 'completed', answered, percentage, resultId };
};

const statusLabel = item => {
  switch (item.status) {
    case 'pending':
      return 'Waiting…';
    case 'running':
      return `Running… (${item.answered || 0} answered, ${Math.round(
        item.percentage || 0
      )}%)`;
    case 'completed':
      return `OK ✓ Completed — ${item.answered} question(s) answered`;
    case 'screened_out':
      return `⚠ Screened out / quota full (after ${item.answered ||
        0} answer(s))`;
    case 'error':
      return `✕ Error: ${item.message}`;
    default:
      return item.status;
  }
};

const statusColor = status => {
  if (status === 'completed') return '#1e7e34';
  if (status === 'screened_out') return '#b8860b';
  if (status === 'error') return '#c0392b';
  if (status === 'running') return '#1f6fb2';
  return '#666';
};

const SurveyCompletionTest = () => {
  const [iterations, setIterations] = useState('5');
  const [surveyId, setSurveyId] = useState('');
  const [userId, setUserId] = useState('');
  const [running, setRunning] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [results, setResults] = useState([]);

  const updateResult = (index, patch) => {
    setResults(prev =>
      prev.map((item, i) => (i === index ? { ...item, ...patch } : item))
    );
  };

  const handleLaunch = async () => {
    const count = parseInt(iterations, 10);
    setGlobalError(null);

    if (!surveyId.trim() || !userId.trim()) {
      setGlobalError('Please enter both a Survey id and a User id.');
      return;
    }
    if (!count || count < 1) {
      setGlobalError('Number of iterations must be a positive number.');
      return;
    }

    setRunning(true);
    setResults(
      Array.from({ length: count }, (_, i) => ({
        index: i + 1,
        status: 'pending',
        answered: 0,
        percentage: 0,
        resultId: null,
        message: null
      }))
    );

    // Resolve the survey language once (used for the test user data).
    let language = 'NL';
    try {
      const campaignData = await gqlUsers(GET_CAMPAIGN, {
        id: surveyId.trim(),
        referrer: userId.trim()
      });
      const { campaign } = campaignData;
      if (!campaign) throw new Error('Campaign not found for this Survey id.');
      language = LANGUAGE_MAP[campaign.language] || 'NL';
    } catch (error) {
      setGlobalError(`Could not load the survey: ${error.message}`);
      setRunning(false);
      setResults([]);
      return;
    }

    for (let i = 0; i < count; i += 1) {
      updateResult(i, { status: 'running', answered: 0, percentage: 0 });
      try {
        // eslint-disable-next-line no-await-in-loop
        const outcome = await runIteration(
          surveyId.trim(),
          userId.trim(),
          language,
          progress => updateResult(i, { status: 'running', ...progress })
        );
        updateResult(i, {
          status: outcome.outcome,
          answered: outcome.answered,
          percentage: outcome.percentage,
          resultId: outcome.resultId
        });
      } catch (error) {
        updateResult(i, { status: 'error', message: error.message });
      }
    }

    setRunning(false);
  };

  const completedCount = results.filter(r => r.status === 'completed').length;
  const finishedCount = results.filter(
    r =>
      r.status === 'completed' ||
      r.status === 'screened_out' ||
      r.status === 'error'
  ).length;

  return (
    <div style={styles.container}>
      <Link to="/development-dashboard" style={styles.backLink}>
        &larr; Back to dashboard
      </Link>
      <h1>Survey completion test</h1>
      <p>
        Automatically completes a survey with random answers, as if a user
        opened the web test link, and reports the status of each iteration.
      </p>

      <div style={styles.form}>
        <div style={styles.field}>
          <span style={styles.label}>Number of iterations</span>
          <Input
            type="number"
            min="1"
            value={iterations}
            onChange={e => setIterations(e.target.value)}
            disabled={running}
          />
        </div>
        <div style={styles.field}>
          <span style={styles.label}>Survey id</span>
          <Input
            type="text"
            value={surveyId}
            onChange={e => setSurveyId(e.target.value)}
            placeholder="Enter survey id"
            disabled={running}
          />
        </div>
        <div style={styles.field}>
          <span style={styles.label}>User id</span>
          <Input
            type="text"
            value={userId}
            onChange={e => setUserId(e.target.value)}
            placeholder="Enter user id"
            disabled={running}
          />
        </div>
        <Button
          onClick={handleLaunch}
          primary
          disabled={running}
          style={styles.launchButton}
        >
          {running ? 'Running…' : 'Launch test'}
        </Button>
      </div>

      {globalError && <p style={styles.errorText}>{globalError}</p>}

      {results.length > 0 && (
        <div style={styles.resultsContainer}>
          <h2>
            Iterations — {completedCount}/{results.length} completed
            {finishedCount < results.length
              ? ` (${finishedCount} finished)`
              : ''}
          </h2>
          <ul style={styles.resultList}>
            {results.map(item => (
              <li key={item.index} style={styles.resultItem}>
                <span style={styles.resultIndex}>#{item.index}</span>
                <span style={{ color: statusColor(item.status) }}>
                  {statusLabel(item)}
                </span>
                {item.resultId && (
                  <span style={styles.resultId}>result: {item.resultId}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px'
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '10px',
    color: '#1f6fb2',
    textDecoration: 'none',
    fontWeight: 600
  },
  form: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: '16px',
    maxWidth: '700px',
    marginBottom: '16px'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '180px',
    flex: '1 1 180px'
  },
  label: {
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#333'
  },
  launchButton: {
    minWidth: '150px',
    height: '38px'
  },
  errorText: {
    color: '#c0392b'
  },
  resultsContainer: {
    marginTop: '20px'
  },
  resultList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    fontSize: '14px'
  },
  resultIndex: {
    fontWeight: 700,
    minWidth: '36px'
  },
  resultId: {
    color: '#999',
    fontSize: '12px',
    marginLeft: 'auto'
  }
};

export default SurveyCompletionTest;
